'use client';

import { Card, CardContent } from "@/components/ui/card";
import Link from "next/link";
import { Company } from "@/app/types/Company";
import { usePathname } from "next/navigation";
import { Check, LockKeyhole, ArrowRight } from "lucide-react"
import { TrackableCompanyTitle } from "./trackable-company-title";
import { Separator } from "@radix-ui/react-dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getDisplayDealScore } from "@/utils/deal-score";
import { buildCompanyDetailsHref } from "@/utils/company-selection";

interface TrackableCompanyCardProps {
    company: Company;
    isPurchased?: boolean;
    isPublished?: boolean;
    size?: 'xsmall' | 'small' | 'large';
    cardClassName?: string;
    isLoading?: boolean;
}

const TrackableCompanyCard = ({ company, isPurchased, isPublished, size, cardClassName, isLoading }: TrackableCompanyCardProps) => {
    const pathname = usePathname();

    const card = <Card className={`flex w-full bg-white md:w-[254px] cursor-pointer p-0 hover:border-black ${cardClassName}`}>
        <CardContent className="flex flex-col justify-center w-full gap-3 p-0 pt-4 md:pt-6 pb-2 relative">
            <div className="pl-4 md:pl-6">
                <TrackableCompanyTitle size={size || 'small'} company={company} isPurchased={isPurchased || false} isLoading={isLoading} />
            </div>
            {size !== 'small' && size !== 'xsmall' && <>
                <Separator className="w-full h-[1px] bg-[#D7D5D2]" />
                {!isPublished && <div className="pl-6 flex text-gray-500 text-sm gap-1 items-center justify-between">Acquiring data<ArrowRight className="ml-auto mr-4" width={16} height={16} /></div>}
                {isPublished && <div className="pl-6 flex text-gray-500 text-sm gap-1 items-center">{isPurchased ? <Check width={'16px'} /> : <LockKeyhole width={'16px'} />}{isPurchased ? 'Unlocked' : 'Locked'}<ArrowRight className="ml-auto mr-4" width={16} height={16} /></div>}
            </>}
            {isPublished && (
                <div className="pl-6 flex items-center gap-2">
                    {(() => {
                        const score = getDisplayDealScore(company);
                        return typeof score === 'number' ? (
                            <span className={
                                score >= 80 ? 'bg-fresh-green/01 text-white px-2 py-1 rounded-md text-sm font-semibold' :
                                    score >= 70 ? 'bg-dark-orange/01 text-white px-2 py-1 rounded-md text-sm font-semibold' :
                                        score >= 50 ? 'bg-fuel-yellow/04 text-white px-2 py-1 rounded-md text-sm font-semibold' :
                                            'bg-fuel-yellow/04 text-white px-2 py-1 rounded-md text-sm font-semibold'
                            }>
                                {score.toFixed(0)}
                            </span>
                        ) : (
                            <span className="text-gray-400 text-sm font-medium">N/A</span>
                        );
                    })()}
                    {Array.isArray(company.topics) && company.topics.length > 0 && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="bg-[#f3f3ff] text-gray-600 rounded-md px-2 py-0.5 text-xs font-medium cursor-pointer">
                                        {company.topics.length} {company.topics.length === 1 ? 'intent' : 'intents'}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <div className="text-xs text-gray-800 max-w-xs whitespace-pre-line">
                                        {company.topics.join(', ')}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            )}
        </CardContent>
    </Card>;

    return pathname === '/onboarding' || pathname === '/welcome' ? <>{card}</> :
        <Link href={buildCompanyDetailsHref(company)} className={`block ${pathname === '/onboarding' ? 'pointer-events-none' : ''}`}>
            {card}
        </Link>;
}

export default TrackableCompanyCard;
